// Function index:
//   boot() — top-level boot loop:
//     1. /api/auth/me — if logged in, skip straight to the menu.
//     2. URL routing: ?build=N opens the builder, ?party=N enters the game,
//        otherwise the title screen (login + main menu).
//     3. The title screen returns { kind: 'play', partyId },
//        { kind: 'build', worldId }, or { kind: 'admin' }; we reflect that in
//        the URL and run the matching mode.
//     4. Builder / admin loop back to the title screen. Game runs until
//        logout/leave.
//   handleBillingReturn() — on load, confirm a returning Stripe checkout
//       (?billing_session=…) and clean the URL.

import { me, type AuthUser, confirmBilling } from './api.js';
import { showTitleScreen } from './screens/title.js';
import { showBuilderScreen } from './screens/builder.js';
import { showAdminScreen } from './screens/admin.js';
import { runGame } from './game/runtime.js';
import { numParam, setUrlParams } from './game/url.js';

async function handleBillingReturn(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const session = params.get('billing_session');
  if (!session) return;
  try {
    const ent = await confirmBilling(session);
    alert(ent.subscription ? `Subscription active: ${ent.subscription.plan_key}` : 'Payment not yet confirmed — it may take a moment.');
  } catch {
    // Swallow: 409 = not paid yet; a 401 (expired session) or transient error
    // is also ignored here — the entitlement webhook reconciles, and the user
    // can re-check from Account → Billing. We still strip the param below.
  } finally {
    params.delete('billing_session');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }
}

async function boot(): Promise<void> {
  await handleBillingReturn();
  let user: AuthUser | null = null;
  try { user = (await me()).user; } catch { /* not logged in */ }
  // Auth state has resolved — drop the boot loader so the title screen can
  // fade in cleanly.
  const loader = document.getElementById('boot-loader');
  if (loader) loader.remove();

  while (true) {
    const params = new URLSearchParams(location.search);
    const buildId = numParam(params.get('build'));
    const partyId = numParam(params.get('party'));
    const admin = params.get('admin') === '1';

    if (admin && user?.role === 'admin') {
      await showAdminScreen(user);
      setUrlParams({});
      continue;
    }
    if (buildId !== null && user) {
      await showBuilderScreen(buildId);
      setUrlParams({});
      continue;
    }
    if (partyId !== null && user) {
      await runGame(user, partyId);
      return;
    }

    const { user: signedIn, choice } = await showTitleScreen(user);
    user = signedIn;
    if (choice.kind === 'play')  setUrlParams({ party: String(choice.partyId) });
    else if (choice.kind === 'build') setUrlParams({ build: String(choice.worldId) });
    else if (choice.kind === 'admin') setUrlParams({ admin: '1' });
  }
}

boot().catch((err) => {
  console.error('boot failed', err);
});
